import { FC, useMemo } from 'react';
import { Box, Typography } from '@mui/material';
import { ResponsiveLine } from '@nivo/line';
import { tokens } from '../../theme';
import type {
  TimelineEntry,
  WebinarGroupedEvaluationData,
} from '../../helpers/webinarStatisticsData';
import Explainer from '../../components/Explainer';
import { KPI_CONCEPTS } from '../../helpers/webinarKpiDefinitions';
import KpiTile from '../../components/auswertungen/KpiTile';
import GroupedSections from '../../components/auswertungen/GroupedSections';

interface GroupedEvaluationsTabProps {
  colors: ReturnType<typeof tokens>;
  data: WebinarGroupedEvaluationData | null;
}

const chartData = (entries: TimelineEntry[]) => {
  const categories = Array.from(new Set(entries.map((e) => e.category)));
  return categories.map((category) => ({
    id: category,
    data: entries
      .filter((e) => e.category === category)
      .sort((a, b) => a.month.localeCompare(b.month))
      .map((e) => ({ x: e.month, y: e.registrations })),
  }));
};

/**
 * Tab „Gruppierte Auswertungen“: dieselben Kanal-Tabellen wie auf der Detailseite, ueber alle
 * Webinare im Zeitraum. Bis 2026-09-14 ordnete dieser Tab Bestellungen per E-Mail zu und zeigte
 * damit u. a. 2,5-fachen erwarteten Umsatz; heute zaehlt er aus denselben Fakten wie die Uebersicht.
 */
const GroupedEvaluationsTab: FC<GroupedEvaluationsTabProps> = ({ colors, data }) => {
  const adTypeChart = useMemo(() => (data ? chartData(data.timeline.byAdType) : []), [data]);
  const blogChart = useMemo(() => (data ? chartData(data.timeline.byBlog) : []), [data]);

  if (!data) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="30vh">
        <Typography color={colors.grey[300]} variant="h5">
          Klicken Sie auf &quot;Laden&quot; um die Daten zu laden
        </Typography>
      </Box>
    );
  }

  const all = data.totals.all;
  const excludedCount = data.webinars_excluded_for_tracking?.length ?? 0;
  const ohneWarnung = excludedCount
    ? ` · ohne ${excludedCount} Webinar(e) mit Tracking-Warnung`
    : '';
  const chartTheme = {
    axis: {
      domain: { line: { stroke: colors.grey[100] } },
      legend: { text: { fill: colors.grey[100] } },
      ticks: {
        line: { stroke: colors.grey[100], strokeWidth: 1 },
        text: { fill: colors.grey[100] },
      },
    },
    legends: { text: { fill: colors.grey[100] } },
  };

  return (
    <Box>
      <Explainer
        title="So lesen Sie diese Auswertung"
        sections={KPI_CONCEPTS.map((c) => ({ label: c.titel, body: c.text }))}
      />

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(min(220px, 100%), 1fr))',
          gap: '15px',
          mb: '24px',
        }}
      >
        <KpiTile metric="registrations" value={all.registrations} />
        <KpiTile
          metric="attendees"
          value={all.attendees}
          sub={ohneWarnung ? ohneWarnung.slice(3) : undefined}
        />
        <KpiTile
          metric="show_up_rate"
          value={all.show_up_rate}
          sub={`${all.attendees.toLocaleString('de-DE')} von ${all.timely.toLocaleString('de-DE')} rechtzeitig Angemeldeten${ohneWarnung}`}
        />
        <KpiTile metric="orders" value={all.orders} />
        <KpiTile metric="registration_conversion_rate" value={all.registration_conversion_rate} />
        <KpiTile
          metric="attendee_conversion_rate"
          value={all.attendee_conversion_rate}
          sub={ohneWarnung ? ohneWarnung.slice(3) : undefined}
        />
        <KpiTile
          metric="cpa_revenue"
          value={all.cpa_revenue}
          sub={
            all.orders_without_cpa
              ? `${all.orders_without_cpa} ${Number(all.orders_without_cpa) === 1 ? 'Anfo' : 'Anfos'} ohne CPA-Wert nicht enthalten`
              : undefined
          }
        />
        <KpiTile metric="ltv_revenue" value={all.ltv_revenue} />
        <KpiTile metric="paid_revenue" value={all.paid_revenue} />
      </Box>

      <GroupedSections data={data} />

      <Box sx={{ mt: 4 }}>
        <Typography variant="h5" sx={{ color: colors.grey[100], mb: 0.5 }}>
          Zeitliche Entwicklung
        </Typography>
        <Typography variant="body2" sx={{ color: colors.grey[300], mb: 2 }}>
          Registrierungen je Kanal, nach Monat des Webinars.
        </Typography>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(min(420px, 100%), 1fr))',
            gap: 3,
          }}
        >
          {[
            { title: 'Nach Ad-Type', series: adTypeChart },
            { title: 'Nach Blog', series: blogChart },
          ].map(({ title, series }) => (
            <Box key={title}>
              <Typography variant="h6" sx={{ color: colors.grey[200], mb: 1 }}>
                {title}
              </Typography>
              <Box
                sx={{
                  height: '350px',
                  backgroundColor: colors.primary[400],
                  p: '20px',
                  borderRadius: '8px',
                }}
              >
                {series.length > 0 ? (
                  <ResponsiveLine
                    data={series}
                    margin={{ top: 20, right: 150, bottom: 50, left: 60 }}
                    xScale={{ type: 'point' }}
                    yScale={{ type: 'linear', min: 0, max: 'auto' }}
                    curve="monotoneX"
                    axisBottom={{ tickRotation: -45 }}
                    axisLeft={{
                      legend: 'Registrierungen',
                      legendOffset: -50,
                      legendPosition: 'middle',
                    }}
                    pointSize={8}
                    pointBorderWidth={2}
                    pointBorderColor={{ from: 'serieColor' }}
                    useMesh
                    legends={[
                      {
                        anchor: 'bottom-right',
                        direction: 'column',
                        translateX: 140,
                        itemWidth: 130,
                        itemHeight: 18,
                        symbolSize: 10,
                        symbolShape: 'circle',
                      },
                    ]}
                    theme={chartTheme}
                  />
                ) : (
                  <Box display="flex" alignItems="center" justifyContent="center" height="100%">
                    <Typography color={colors.grey[300]}>Keine Verlaufsdaten</Typography>
                  </Box>
                )}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default GroupedEvaluationsTab;
